import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { updateSelectedDevices } from '../recorderSlice';
import {
  fetchAvailableDevices,
  fetchUserMediaThunk,
  initializeUploadThunk,
  startRecordingThunk,
  createBackupRecordThunk,
} from '../thunk';
import type { RecorderState } from '../store';
import type { AppDispatch } from '../../store';
import logger from '../../lib/logger';
import trackRecorderError from '../utils/errorTracking';

const SkipDeviceSelection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [showNoAudioAlert, setShowNoAudioAlert] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const { availableDevices, selectedDevices, upload, status, settings } =
    useSelector((state: RecorderState) => state.recorder);
  const { audioinput } = selectedDevices;
  const microphoneAudio = settings?.audioSettings?.microphoneAudio;

  useEffect(() => {
    const handleDeviceChange = () => {
      dispatch(fetchAvailableDevices({ camera: false, microphone: true }));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchAvailableDevices({ camera: false, microphone: true }));
      }
    };

    // Initial fetch
    dispatch(fetchAvailableDevices({ camera: false, microphone: true }));

    // Setup event listeners
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  // Helper to check if upload is already initialized
  const isUploadInitialized = () => {
    return upload.uploadId && upload.fileId;
  };

  const findWorkingAudioDevice = async () => {
    // Prevent multiple initialization attempts
    if (hasInitialized || isUploadInitialized() || status !== 'ready') {
      return false;
    }
    console.log('🔍 findWorkingAudioDevice:', {
      microphoneAudio,
      deviceId: audioinput.deviceId,
    });
    // If mic is explicitly disabled (e.g., Record Tab Audio mode), skip mic auto-selection
    // and proceed directly to start recording without a mic device
    if (!microphoneAudio && audioinput.deviceId === 'none') {
      setHasInitialized(true);
      const initResult = (await dispatch(initializeUploadThunk())) as any;
      if (initResult.type === initializeUploadThunk.rejected.type) {
        logger.error('Upload initialization failed, not starting recording');
        setHasInitialized(false);
        trackRecorderError({
          operation: 'initialization',
          errorType: 'InitializationError',
          errorMessage:
            'Upload initialization failed in skip device selection (mic disabled)',
          additionalContext: {
            source: 'skip_device_selection',
            microphoneAudio: false,
          },
        });
        setShowNoAudioAlert(true);
        return false;
      }
      await dispatch(startRecordingThunk());
      await dispatch(createBackupRecordThunk());
      setShowNoAudioAlert(false);
      return true;
    }

    if (
      audioinput.deviceId === 'none' &&
      availableDevices.audioinput.length > 0
    ) {
      const workingDevice = await availableDevices.audioinput.reduce(
        async (prevPromise, device) => {
          const prev = await prevPromise;
          if (prev) return prev;

          const result = (await dispatch(
            fetchUserMediaThunk({
              audio: { deviceId: device.deviceId },
            }),
          )) as unknown as {
            payload: {
              trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
            };
          };

          const hasAudioTrack = result?.payload?.trackInfo.some(
            (track) => track.kind === 'audio' && track.label === device.label,
          );

          return hasAudioTrack ? device : null;
        },
        Promise.resolve(null),
      );

      if (workingDevice) {
        await dispatch(
          updateSelectedDevices({ audioinput: workingDevice.deviceId }),
        );
        // FIXED: Don't start recording if initialization fails
        setHasInitialized(true);
        const initResult = (await dispatch(initializeUploadThunk())) as any;
        if (
          // @ts-ignore - TS doesn't know about the type of the action
          initResult.type === initializeUploadThunk.rejected.type
        ) {
          logger.error('Upload initialization failed, not starting recording');
          setHasInitialized(false); // Reset flag on failure
          // FIXED: Track initialization failure in PostHog for monitoring
          trackRecorderError({
            operation: 'initialization',
            errorType: 'InitializationError',
            errorMessage:
              'Upload initialization failed in skip device selection',
            additionalContext: {
              source: 'skip_device_selection',
              selectedAudioDevice: audioinput.deviceId,
              hasAvailableDevices: availableDevices.audioinput.length > 0,
            },
          });
          setShowNoAudioAlert(true);
          return false;
        }

        await dispatch(startRecordingThunk());
        await dispatch(createBackupRecordThunk());
        setShowNoAudioAlert(false);
        return true;
      }

      logger.log('No audio devices with working audio tracks found');
      setShowNoAudioAlert(true);
      return false;
    }

    // If we already have a device selected, verify it works
    if (audioinput.deviceId !== 'none') {
      const result = (await dispatch(
        fetchUserMediaThunk({
          audio: { deviceId: audioinput.deviceId },
        }),
      )) as unknown as {
        payload: {
          trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
        };
      };

      const hasAudioTrack = result?.payload?.trackInfo.some(
        (track) => track.kind === 'audio',
      );

      if (hasAudioTrack) {
        // FIXED: Don't start recording if initialization fails
        setHasInitialized(true);
        const initResult = (await dispatch(initializeUploadThunk())) as any;
        if (
          // @ts-ignore - TS doesn't know about the type of the action
          initResult.type === initializeUploadThunk.rejected.type
        ) {
          logger.error('Upload initialization failed, not starting recording');
          setHasInitialized(false); // Reset flag on failure
          // FIXED: Track initialization failure in PostHog for monitoring
          trackRecorderError({
            operation: 'initialization',
            errorType: 'InitializationError',
            errorMessage:
              'Upload initialization failed in skip device selection',
            additionalContext: {
              source: 'skip_device_selection',
              selectedAudioDevice: audioinput.deviceId,
              hasAvailableDevices: availableDevices.audioinput.length > 0,
            },
          });
          setShowNoAudioAlert(true);
          return false;
        }

        await dispatch(startRecordingThunk());
        await dispatch(createBackupRecordThunk());
        setShowNoAudioAlert(false);
        return true;
      }
      setShowNoAudioAlert(true);
      return false;
    }

    // If no device is selected and no working device found
    setShowNoAudioAlert(true);
    return false;
  };

  useEffect(() => {
    // Only trigger when devices are successfully loaded and we haven't initialized yet
    if (
      availableDevices.status === 'succeeded' &&
      !hasInitialized &&
      !isUploadInitialized() &&
      status === 'ready'
    ) {
      findWorkingAudioDevice();
    }
  }, [availableDevices.status, hasInitialized, status]);

  // Reset initialization flag when recorder status changes back to initializing
  useEffect(() => {
    if (status === 'initializing') {
      setHasInitialized(false);
    }
  }, [status]);

  if (showNoAudioAlert) {
    return (
      <div className="p-2">
        <Alert variant="warning" className="rounded-lg">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t(
              'recorder.skipDeviceSelection.noAudioAlert',
              'No working audio device detected. Please check your microphone settings.',
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return null;
};

export default SkipDeviceSelection;
